import type { Question } from 'types';
import { normalizeQuestionType } from './questionTypeUtils';

const supportedTypes = new Set([
  'text_or_audio', 'text_list', 'rating', 'boolean', 'multiple_choice',
  'multiple_select', 'drag_and_drop_ranking',
]);

export const supportsQuestionGeneration = (questionType: string): boolean =>
  supportedTypes.has(normalizeQuestionType(questionType));

export const hasQuestionGeneration = (question?: Question | null): boolean =>
  Boolean(question && supportsQuestionGeneration(question.question_type_display ?? question.question_type)
    && question.metadata?.ai_generation?.enabled === true);

export const hasQuestionGenerationCorrectness = (
  metadata: Record<string, unknown> | undefined,
  options: ReadonlyArray<{ is_correct?: boolean }> = [],
): boolean => {
  const correctIndexes = metadata?.correct_option_indexes;
  return ['correct_boolean', 'correct_value', 'correct_boolean_value'].some(
    (key) => metadata?.[key] !== undefined && metadata[key] !== null,
  ) || (Array.isArray(correctIndexes) && correctIndexes.length > 0)
    || options.some((option) => option.is_correct === true);
};

export const validateQuestionGeneration = (
  metadata: Record<string, unknown> | undefined,
  questionType: string,
): string | null => {
  const value = metadata?.ai_generation;
  if (value === undefined) return null;
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return 'Personalized question settings must be a JSON object.';
  }
  const config = value as Record<string, unknown>;
  if (Object.keys(config).some((key) => !['enabled', 'prompt'].includes(key))) {
    return 'Personalized question settings contain an unsupported field.';
  }
  if (typeof config.enabled !== 'boolean') return 'Personalized question settings require enabled to be true or false.';
  if (config.prompt !== undefined && typeof config.prompt !== 'string') return 'Personalization instructions must be text.';
  if (typeof config.prompt === 'string' && Array.from(config.prompt).length > 6000) return 'Personalization instructions must be 6,000 characters or fewer.';
  if (!config.enabled) return null;
  if (!supportsQuestionGeneration(questionType)) return 'Personalized questions are not available for this question type.';
  if (typeof config.prompt !== 'string' || !config.prompt.trim()) return 'Add instructions for personalizing the question.';
  if (hasQuestionGenerationCorrectness(metadata)) return 'Personalized questions cannot have a fixed correct answer.';
  return null;
};
